import React, { useState } from "react";
import {
  Box,
  IconButton,
  Center,
  Spinner,
  HStack,
  Icon,
  Divider,
  Text,
  Tag,
  TagLabel,
  Flex,
  MenuList,
  Menu, 
  MenuItem,
  MenuButton,
  Tooltip,
} from "@chakra-ui/react";
import { FaTimes, FaFilter, FaChevronDown, FaDownload } from "react-icons/fa";
import DataChart from "./DataChart";
import DataTable from "./DataTable";
import PromptString from "./PromptString";
import { NodeInsights } from "../../containers/Interfaces";
import { BiMath } from "react-icons/bi";
import { TbCalculatorOff } from "react-icons/tb";
import { getOperatorSymbol } from "../../utils/analysisUtils";
import { getDateFilterName } from "../../utils/nameUtils";
import { IoCopyOutline } from "react-icons/io5";
import { FilterProto } from "build-ts/metadata";
import DataTableMKG from "./data_table/DataTableMKG";
import { ColumnType } from "build-ts/enums";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { buildActualsMap, getNonMeasureData, isColumnMissing, extractRawDataFromNode, copyRawTableToClipboard, downloadRawTableAsExcel } from "./data_table/dataTableUtils";
import { useAnalysisTreeContext } from "context/AnalysisTreeContext";
import { BsFillInfoSquareFill } from "react-icons/bs";
import { useUser } from "context/UserContext";

interface DataDisplayComponentProps {
  index: number;
  tableNumber?: number;
  nodeDataLoader: boolean;
  node: NodeInsights;
  isAdmin: boolean;
  onClose: () => void;
  nodeInfoMap: Map<string, NodeInsights> | null;
  isChatOpen: boolean;
  selectedNodeId: string | null;
}

const DataDisplayComponent: React.FC<DataDisplayComponentProps> = ({
  index,
  tableNumber,
  nodeDataLoader,
  node,
  isAdmin,
  onClose,
  nodeInfoMap,
  isChatOpen,
  selectedNodeId,
}) => {
  const view = "table";
  const { treePannelLoader, metrics} = useAnalysisTreeContext();
  const [showHiddenColumns, setShowHiddenColumns] = useState(false);
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);
  const { defaultTimeZone, quarterStartMonth } = useUser();

  // Finding the number of rows in table
  const actualsMap =  buildActualsMap(node?.current_node?.kg_observation_data!);
  const { nonMeasureColumns } = getNonMeasureData(node, defaultTimeZone, quarterStartMonth);

  let noOfRows = 0;
  if(nonMeasureColumns.length === 0) {
    noOfRows = Object.keys(actualsMap).length;
  }
  else {
    noOfRows = actualsMap[Object.keys(actualsMap)[0]]?.actual_value?.length || 0;
  }
  
  // Use tableNumber if provided, otherwise use index
  const displayNumber = tableNumber !== undefined ? tableNumber : index + 1;
   
  // Check if the current node is selected
  const isSelected = selectedNodeId === node.current_node.id;

  // function to render the filters
  const renderFilters = () => {

    let filters: FilterProto[] = [];
  
    if (node.current_node?.observation?.content) {
      filters = node.current_node.observation.content.filters || [];
    } 
    // If not, check if node.current_node.kg_observation is present
    else if (node.current_node?.kg_observation_data?.kg_observation) {
      filters = node.current_node.kg_observation_data?.kg_observation?.filters || [];
    }

    if(filters?.length === 0 || !filters) return null;

    const groupedFilters = filters.reduce((acc, filter) => {
      let colunName = "";
      if(filter?.logical_column_header?.name) {
        colunName = filter?.logical_column_header?.name;
      } else if (filter?.kg_column_header?.name) {
        colunName = filter?.kg_column_header?.name;
      }
      let key = "";
      if(filter?.logical_column_header?.column_type === ColumnType.TEMPORAL) {
        key = "DATE_FILTERS";
      } else {
        key = colunName;
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(filter);
      return acc;
    }, {} as Record<string, FilterProto[]>);
    
  
    return (
      <Box
        overflowX="auto"
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": {
            height: "3px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E0",
            borderRadius: "3px",
          },
        }}
      >
        <HStack spacing={isChatOpen ? 1 : 2} py={1}>
          {Object.entries(groupedFilters).map(([key, filtersGroup], index) => {
            // Special handling for date filters
            if (key === "DATE_FILTERS") {
              return (
                <Tag
                  key={index}
                  size={isChatOpen ? "xs" : "sm"}
                  borderRadius="md"
                  variant="solid"
                  bg="gray.50"
                  color="gray.800"
                  borderWidth="1px"
                  borderColor="gray.300"
                  p={1}
                  minW="fit-content"
                >
                  <TagLabel 
                    fontSize={isChatOpen ? "xs" : "sm"}
                    textAlign="left"
                    whiteSpace="normal"
                  >
                    <Box as="span" fontWeight={isChatOpen ? "600" : "700"}>
                      Date
                    </Box>
                    <Box as="span" fontWeight="normal">
                      {filtersGroup.map((filter, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && " | "}
                          {" "}
                          {getOperatorSymbol(filter?.operator!)}
                          {" "}
                          {filter?.values?.length > 0 
                            ? filter.values?.map((value: any) => value.toString().trim()).join(", ")
                            : filter?.date_filter?.absolute_date_filter ? getDateFilterName(filter?.date_filter?.absolute_date_filter!) : getDateFilterName(filter?.date_filter!)}
                        </React.Fragment>
                      ))}
                    </Box>
                  </TagLabel> 
                </Tag>
              );
            }
            
            // Regular handling for non-date filters
            return filtersGroup.map((filter, i) => (
              <Tag
                key={`${index}-${i}`}
                size={isChatOpen ? "xs" : "sm"}
                borderRadius="md"
                variant="solid"
                bg="gray.50"
                color="gray.800"
                borderWidth="1px"
                borderColor="gray.300"
                p={1}
                minW="fit-content"
              >
                <TagLabel 
                  fontSize={isChatOpen ? "xs" : "sm"}
                  textAlign="left"
                  whiteSpace="normal"
                >
                  <Box as="span" fontWeight={isChatOpen ? "600" : "700"}>
                    {filter?.kg_column_header?.name}
                  </Box>
                  <Box as="span" fontWeight="normal">
                    {" "}
                    {getOperatorSymbol(filter?.operator!)}
                    {" "}
                    {filter?.values?.length > 0 
                      ? filter.values?.map((value: any) => value.toString().trim()).join(", ")
                      : filter?.date_filter?.absolute_date_filter ? getDateFilterName(filter?.date_filter?.absolute_date_filter!) : getDateFilterName(filter?.date_filter!)}
                  </Box>
                </TagLabel> 
              </Tag>
            ));
          })}
        </HStack>
      </Box>
    );
  };

  return (
    <Box
      bg="transparent"
      position="relative"
      overflow="hidden"
      p={0}
      width="100%"
      height="auto"
      borderWidth={isSelected ? 1 : 1}  // Add a highlighted border when this node is selected
      borderColor={isSelected ? "purple.700" : "gray.300"}
      borderRadius="md"
      transition="all 0.2s ease-in-out"
    >
      {!nodeDataLoader && (
        <HStack 
          justify="space-between" 
          align="center" 
          w="100%" 
          p={0}
          borderBottomWidth={isTableCollapsed ? 0 : 1}
          borderColor="gray.200"
          spacing={0}
          bg="transparent"
          _hover={{ bg: "gray.100", cursor: "pointer" }}
          onClick={() => setIsTableCollapsed(!isTableCollapsed)}  //clicking on the header collapses the table
        >
          <HStack spacing={1} pl={1} flex={1} minWidth={0}>
            <Flex
              align="center"
              justify="center"
              bg="purple.700"
              color="white"
              borderRadius="full"
              w={isChatOpen ? "21px" : "24px"}
              h={isChatOpen ? "21px" : "24px"}
              fontSize={isChatOpen ? "xs" : "sm"}
              fontWeight="bold"
              flexShrink={0}
              borderWidth={2}
              borderColor="purple.700"
            >
              <Text mb={0}>{displayNumber}</Text>
            </Flex>
            <Box flex={1} minWidth={0}>
              <Box
                overflowX="auto"
                css={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#CBD5E0 transparent",
                  "&::-webkit-scrollbar": {
                    height: "3px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#CBD5E0",
                    borderRadius: "3px",
                  },
                }}
              >
                <HStack spacing={2}>
                  <Tag
                    size={isChatOpen ? "xs" : "sm"}
                    borderRadius="md"
                    variant="solid"
                    bg="gray.50"
                    color="gray.800"
                    borderWidth="1px"
                    borderColor="gray.300"
                    p={1}
                  >
                    <HStack spacing={1}>
                      <Icon as={FaFilter} boxSize={isChatOpen ? 3 : 4} color="purple.700" />
                      {!isChatOpen && <Text fontSize={isChatOpen ? "2xs" : "xs"} fontWeight="bold" mb={0} color="purple.700">FILTERS</Text>}
                    </HStack>
                  </Tag>
                  <Divider orientation="vertical" height="2vh" my={0} borderColor="gray.400" />
                  {renderFilters()}
                </HStack>
              </Box>
            </Box>
          </HStack>
          <HStack spacing={0} flexShrink={0} onClick={(e) => e.stopPropagation()}>
            {noOfRows && noOfRows > 0 && (
              <Tooltip
                label={`${noOfRows} rows present`}
                placement="left"
                hasArrow
                bg="gray.700"
                color="white"
                fontSize="sm"
                p={2}
                borderRadius="md"
                maxWidth="200px"
              >
                <IconButton
                  size="xl"
                  variant="ghost"
                  colorScheme="gray"
                  aria-label="No of rows present"
                  icon={< Icon as={BsFillInfoSquareFill} boxSize={5} color="gray.500" />}
                  _hover={{ bg: "gray.100" }}
                />
              </Tooltip>
            )}
          {/* Missing column warning Icon Button */}
          {isAdmin && !isTableCollapsed && node.current_node.kg_observation_data?.missing_columns && node.current_node.kg_observation_data?.missing_columns.length > 0 && (
            <Tooltip
              label={
                <Box whiteSpace="pre-line">
                  {isColumnMissing(node, node.current_node.kg_observation_data!)}
                </Box>
              }
              placement="top"
              hasArrow
            >
              <IconButton 
                aria-label="Filters"
                icon={<WarningTwoIcon boxSize="20px" color="yellow.500" opacity={0.7} />}
                size="xs"
                variant="ghost"
                minW="20px"
                height="20px"
                pr={2}
                p={0}
              />
            </Tooltip>
          )}
          {/* Analysis Columns Icon Button */}
          {isAdmin && !isTableCollapsed && (
              <IconButton
                size="xs"
                color="gray.500"
                bg="transparent"
                mr={2}
                _hover={{ bg: "gray.50", color: "gray.800" }}
                aria-label={
                  showHiddenColumns
                    ? "Hide Special Columns"
                    : "Show Special Columns"
                }
                icon={showHiddenColumns ? 
                  <Icon as={TbCalculatorOff} boxSize={5} /> : 
                  <Icon as={BiMath} boxSize={5} />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setShowHiddenColumns((prev) => !prev);
                }}
              />
            )}

            {/* Copy Icon Button and Download Icon Button*/}
            {!isTableCollapsed && (
              <Menu placement="bottom-end" gutter={2} closeOnSelect={true}>
                <MenuButton
                  as={IconButton}
                  size="xs"
                  color="gray.500"
                  bg="transparent"
                  _hover={{ bg: "gray.50", color: "gray.800" }}
                  aria-label="Table options"
                  icon={
                    <HStack spacing={0}>
                      <Icon as={IoCopyOutline} boxSize={5} />
                      <Icon as={FaChevronDown} boxSize={3} ml={0.5} />
                    </HStack>
                  }
                  onClick={(e) => e.stopPropagation()}
                  mr={1}
                  title="Table options"
                />
                <MenuList 
                  minW="100px" 
                  zIndex={1000} 
                  shadow="lg" 
                  border="1px solid" 
                  borderColor="gray.200"
                  p={1}
                  position="relative"
                >
                  <MenuItem 
                    icon={<Icon as={IoCopyOutline} boxSize={4} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      const rawData = extractRawDataFromNode(node, defaultTimeZone, quarterStartMonth);
                      copyRawTableToClipboard(rawData);
                    }}
                  fontSize="sm"
                  >
                    Copy to clipboard
                  </MenuItem>
                  <MenuItem 
                    icon={<Icon as={FaDownload} boxSize={4} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      const rawData = extractRawDataFromNode(node, defaultTimeZone, quarterStartMonth);
                      const fileName = `table_data_${node?.current_node?.id?.substring(0, 8) || "export"}.xlsx`;
                      downloadRawTableAsExcel(rawData, fileName);
                    }}
                    fontSize="sm"
                  >
                    Download as Excel
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
            {/* Close Icon Button */}
            <IconButton
              icon={<Icon as={FaTimes} boxSize={4} />}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="Close"
              size="xs"
              color="red.400"
              colorScheme="gray"
              variant="ghost"
            />
          </HStack>
        </HStack>
      )}
      {/* Data Table */}
      <Box
        flex="1"
        position="relative"
        overflow="hidden"
        p={0}
        width="100%"
        maxHeight={isTableCollapsed ? "0" : "330px"} // Match the TableContainer maxHeight
        height={isTableCollapsed ? "0" : "auto"}
        opacity={isTableCollapsed ? 0 : 1}
        transition="all 0.3s ease-in-out"
        visibility={isTableCollapsed ? "hidden" : "visible"} // Add visibility transition
        mb={isTableCollapsed ? 0 : 1}
      >
        {nodeDataLoader ? (
            <Center height="100%">
              <Spinner size="xl" color="purple" />
            </Center>
          ) : node && node?.current_node.analysed_data?.observation?.content?.observation_columns && 
              node?.current_node.analysed_data?.column_values_list ? (
            view === "table" ? (
              <DataTable
                key={node?.current_node?.id}
                node={node}
                showColumn={isAdmin}
                showHiddenColumns={showHiddenColumns}
                onToggleHiddenColumns={() => setShowHiddenColumns(prev => !prev)}
                isChatOpen={isChatOpen}
              />
            ) : view === "chart" ? (
              <DataChart key={node?.current_node.id} data={node} />
            ) : (
              <PromptString 
                node={node}
                updateNodeInfoMap={(updatedNode) => {
                  nodeInfoMap?.set(node?.current_node?.id!, updatedNode);
                }}
              />
            )
          ) : node && node.current_node.kg_observation && node.current_node.kg_observation_data ? (
            <Box 
              flex="1" 
              overflow="auto"
              maxHeight="calc(50vh - 100px)"
              onWheel={(e) => {
                e.stopPropagation();
                const target = e.currentTarget;
                target.scrollTop += e.deltaY;
              }}
            >
              <DataTableMKG 
                node={node}
                isChatOpen={isChatOpen}
                metrics={metrics}
                treePannelLoader={treePannelLoader}
              />
            </Box>
          ) : null}
      </Box>
    </Box>
  );
};

export default DataDisplayComponent;
